import { useEffect } from 'react';
import { App, Button, Form, Input, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { AddressFields } from '@/components/AddressFields';
import { useIsMobile } from '@/context/IsMobileContext.tsx';
import { useUpdateProfile } from '@/hooks/useUpdateProfile.ts';
import type { TUser } from '@/services/auth/auth.types.ts';
import { SURFACE } from '@/styles/surfaces.ts';
import { WizardProgress } from './WizardProgress.tsx';

const { Title, Text } = Typography;

interface IPropertyDetailsStepProps {
  user: TUser;
  onBack: () => void;
  onDone: () => void;
}

export const PropertyDetailsStep = ({ user, onBack, onDone }: IPropertyDetailsStepProps) => {
  const [form] = Form.useForm();
  const { notification } = App.useApp();
  const { isMobile } = useIsMobile();

  const { updateProfile, isPending } = useUpdateProfile({
    onSuccess: onDone,
    onError: (error) => {
      notification.error({
        message: 'Could Not Save',
        description: error.data?.message?.[0] || 'Failed to save your details. Please try again.',
      });
    },
  });

  useEffect(() => {
    form.setFieldsValue({
      property_address: user.profile?.property_address || '',
      property_apt_unit: user.profile?.property_apt_unit || '',
      property_city: user.profile?.property_city || '',
      // Empty string, not undefined, counts as a value to Ant Design's
      // Select, so State rendered blank instead of showing its "Select state"
      // placeholder.
      property_state: user.profile?.property_state || undefined,
      property_zip_code: user.profile?.property_zip_code || '',
      phone_number: user.profile?.phone_number?.replace('+', '') || '',
    });
  }, [user, form]);

  const handleFinish = (values: {
    property_address: string;
    property_apt_unit: string;
    property_city: string;
    property_state: string;
    property_zip_code: string;
    phone_number: string;
  }) => {
    updateProfile({
      ...values,
      phone_number: `+${values.phone_number}`,
    });
  };

  return (
    <div style={{ padding: isMobile ? '22px 20px 0' : '30px 34px 0' }}>
      <WizardProgress current={1} />

      <Title
        level={isMobile ? 4 : 3}
        style={{
          marginTop: 0,
          marginBottom: 6,
          color: SURFACE.text.strong,
          letterSpacing: '-0.02em',
        }}
      >
        Where is your home?
      </Title>
      <Text
        style={{
          display: 'block',
          marginBottom: isMobile ? 20 : 26,
          color: SURFACE.text.muted,
          fontSize: isMobile ? 14 : 15,
          lineHeight: 1.6,
        }}
      >
        We use your address to tailor your survey and schedule your walkthrough.
      </Text>

      <Form
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        scrollToFirstError
        requiredMark='optional'
      >
        <AddressFields form={form} required showFieldIcons={false} />

        <Form.Item
          name='phone_number'
          label='Phone Number'
          rules={[{ required: true, message: 'Please enter your phone number' }]}
          extra={
            <Text style={{ fontSize: 12, color: SURFACE.text.faint }}>
              Include the country code, without the plus.
            </Text>
          }
        >
          <Input addonBefore='+' placeholder='1 (123) 456-7890' inputMode='tel' />
        </Form.Item>

        {/* Reads as a footer rather than two buttons floating after the last
            field, and stays put when the form scrolls on a short viewport. */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            marginTop: isMobile ? 22 : 28,
            marginLeft: isMobile ? -20 : -34,
            marginRight: isMobile ? -20 : -34,
            padding: isMobile ? '14px 20px' : '16px 34px',
            borderTop: `1px solid ${SURFACE.line.hair}`,
            background: 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
        >
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            block={isMobile}
            disabled={isPending}
          >
            Back
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            loading={isPending}
            block={isMobile}
            style={{ minWidth: isMobile ? undefined : 160 }}
          >
            Continue
          </Button>
        </div>
      </Form>
    </div>
  );
};
